import { SelectedMenuItemFull } from '@/types';
import { CartAddRequestModel, CartEditRequestModel, CartItemAddRequestModel } from '@/models/CartRequestModel';
import { MenuExtraListItemModel } from '@/models/MenuExtraListItemModel';
import ActionTypes from '@/constants/action-types';
import store from '@/store';
import { CartItemModel } from '@/models/CartModel';
import { MenuItemModel } from '@/models/MenuItemModel';
import { isMultiple } from '@/utils/menu-popup-utils';
import { MenuItemSizeModel } from '@/models/MenuItemSizeModel';

/**
 * Save or update cart item.
 * @param saveModel: {SelectedMenuItemFull}
 * @return {Promise<void>}
 */
export async function saveOrUpdateCartItem(saveModel: SelectedMenuItemFull): Promise<void> {
  const update = saveModel.cartItemId !== undefined;

  const cartItem: CartItemAddRequestModel = {
    unitPrice: Number(saveModel.selectedSize.price),
    quantity: saveModel.count,
    note: saveModel.note || '',
    menuId: saveModel.menuItem.menuId,
    menuItemSizeId: saveModel.selectedSize?.id,
    menuItemId: saveModel.menuItem.id,
    extras: saveModel.selectedExtras.flat().filter(Boolean).map((e: MenuExtraListItemModel | undefined) => {
      const extra = e as MenuExtraListItemModel;
      return {
        quantity: 1,
        unitPrice: Number(extra.price),
        itemExtraListId: extra.itemExtraListId,
        itemExtraListItemId: extra.id,
      };
    }),
  };

  if (update) {
    const cartModel: CartEditRequestModel = {
      merchantId: saveModel.merchantId,
      cartItems: [{
        cartItemId: saveModel.cartItemId as number,
        newItem: cartItem,
      }],
    };

    await store.dispatch(`cart/${ActionTypes.EditCart}`, cartModel);
  } else {
    const cartModel: CartAddRequestModel = {
      merchantId: saveModel.merchantId,
      cartItems: [cartItem],
    };

    await store.dispatch(`cart/${ActionTypes.AddCart}`, cartModel);
  }
}

/**
 * Create and return {SelectedMenuItemFull} based on cartItem and menuItem
 * @param cartItem: {CartItemModel}
 * @param menuItem: {MenuItemModel}
 * @param merchantId: {number}
 * @return {SelectedMenuItemFull}
 */
export function createSelectedMenuItemFull(
  cartItem: CartItemModel,
  menuItem: MenuItemModel,
  merchantId: number,
): SelectedMenuItemFull {
  const selectedExtras: Array<MenuExtraListItemModel[] | MenuExtraListItemModel | undefined> = [];

  const cartItemExtraIds: number[] = cartItem.extras.map((e) => e.itemExtraListItemId);

  menuItem.extraLists.forEach((e) => {
    if (e.itemExtraListItems?.length) {
      if (isMultiple(e)) {
        const extras = e.itemExtraListItems.filter((l) => cartItemExtraIds.includes(l.id));
        selectedExtras.push(extras);
      } else {
        selectedExtras.push(e.itemExtraListItems[0]);
      }
    } else {
      selectedExtras.push(undefined);
    }
  });

  return {
    menuItem,
    selectedExtras,
    merchantId,
    cartItemId: cartItem.id,
    count: cartItem.quantity,
    note: cartItem.note,
    selectedSize: menuItem.sizes.find((e) => e.id === cartItem.menuItemSizeId) as MenuItemSizeModel,
  };
}
